import React from 'react';
import { useIntl } from 'react-intl';
import type { UserProfileData } from '@contexts/AuthContextStore';
import ChipPicker from '../components/ChipPicker';
import { CAM_OPTIONS, MES_OPTIONS, MFG_SETUP_OPTIONS } from '../options';
import {
  SectionStack,
  StepSubtitle,
  StepTitle,
} from '../Onboarding.styled';

interface StepManufacturingStackProps {
  data: UserProfileData;
  errors: Partial<Record<keyof UserProfileData, string>>;
  update: (patch: Partial<UserProfileData>) => void;
}

/**
 * Step 4 — manufacturing stack. All chip groups are multi-select; users
 * commonly run more than one manufacturing setup (e.g. in-house CNC plus
 * outsourced casting), so we let them pick as many as apply.
 */
const StepManufacturingStack: React.FC<StepManufacturingStackProps> = ({
  data,
  errors,
  update,
}) => {
  const intl = useIntl();
  return (
    <div data-testid="onboarding-step-mfgStack">
      <StepTitle>
        {intl.formatMessage({ id: 'onboarding.mfgStack.title' })}
      </StepTitle>
      <StepSubtitle>
        {intl.formatMessage({ id: 'onboarding.mfgStack.subtitle' })}
      </StepSubtitle>

      <SectionStack>
        <ChipPicker
          dataTestId="onboarding-cam"
          label={intl.formatMessage({ id: 'onboarding.mfgStack.cam' })}
          options={CAM_OPTIONS}
          columns={2}
          withOther
          value={data.camSoftware ?? []}
          otherValue={data.camOther}
          otherError={errors.camOther}
          onChange={(next) => update({ camSoftware: next })}
          onOtherChange={(next) => update({ camOther: next })}
        />
        <ChipPicker
          dataTestId="onboarding-mfgSetup"
          label={intl.formatMessage({ id: 'onboarding.mfgStack.setup' })}
          options={MFG_SETUP_OPTIONS}
          columns={2}
          value={data.mfgSetup ?? []}
          otherValue={data.mfgSetupOther}
          otherError={errors.mfgSetupOther}
          onChange={(next) => update({ mfgSetup: next })}
          onOtherChange={(next) => update({ mfgSetupOther: next })}
        />
        <ChipPicker
          dataTestId="onboarding-mes"
          label={intl.formatMessage({ id: 'onboarding.mfgStack.mes' })}
          options={MES_OPTIONS}
          columns={2}
          withOther
          value={data.mes ?? []}
          otherValue={data.mesOther}
          otherError={errors.mesOther}
          onChange={(next) => update({ mes: next })}
          onOtherChange={(next) => update({ mesOther: next })}
        />
      </SectionStack>
    </div>
  );
};

export default StepManufacturingStack;
